import { launchScorm } from "../services/ScormService";

/**
 * Shared vocabulary and navigation for assigned content.
 *
 * Every surface that shows a plan item — the dashboard hero, the plan page, a
 * learning-path step — routes through `openAssignedItem` so they all honour the
 * SAME deep-link contract with the section workspace. That contract is the
 * `openTarget` key below: the workspace consumes it once on mount to land on
 * the exact module/lesson instead of a section default. Duplicating this logic
 * is how "Open" starts opening the wrong thing.
 */

/** Human labels for the server's `contentType` values (Assignment model). */
export const TYPE_LABEL = {
  "ppt-module": "Module",
  "ppt-item": "Lesson",
  video: "Video",
  assessment: "Assessment",
  "learning-path": "Learning path",
  scorm: "SCORM",
};

/** Completion percentage for any plan item, single or path. */
export const itemPercent = (item) => {
  if (!item) return 0;
  if (item.contentType === "learning-path") {
    const { done = 0, total = 0 } = item.progress || {};
    return total > 0 ? Math.round((done / total) * 100) : 0;
  }
  return Math.round(item.percent || 0);
};

export const isOverdue = (item) =>
  !!item?.dueDate &&
  item.status !== "completed" &&
  new Date(item.dueDate) < new Date();

export const formatDueDate = (value) =>
  new Date(value).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

/**
 * The verb that fits an item's state — "Start" reads wrong on something the
 * learner already finished, and "Open" tells them nothing about where they are.
 */
export const openLabel = (status) =>
  status === "completed" ? "Review" : status === "in-progress" ? "Resume" : "Start";

/**
 * Open a plan item in the viewer that owns it.
 *
 * SCORM opens in the backend-hosted player via a launch ticket (first-party
 * cookie, correct SCORM API discovery). Everything else reuses the existing
 * learner flow: select the section, hand the workspace an exact target, go.
 */
export const openAssignedItem = (item, navigate) => {
  if (!item?.available) return false;

  if (item.contentType === "scorm") {
    launchScorm(item.contentId);
    return true;
  }

  if (item.section) {
    localStorage.setItem("section", item.section);
  }
  localStorage.setItem(
    "openTarget",
    JSON.stringify({
      contentType: item.contentType,
      contentId: item.contentId,
      pptItemId: item.pptItemId,
    })
  );
  navigate("/modules");
  return true;
};

/**
 * The one item worth resuming, for the "continue where you left off" hero.
 *
 * Preference order is what a learner would actually pick up: something already
 * started and still open, then something overdue, then the nearest deadline,
 * then anything unstarted. Returns null when there is genuinely nothing to
 * continue, so the hero can say so rather than inventing an item.
 */
export const pickResumeItem = (items = []) => {
  const open = items.filter((i) => i.available && i.status !== "completed");
  if (open.length === 0) return null;

  const byDueDate = (a, b) => {
    if (a.dueDate && b.dueDate) return new Date(a.dueDate) - new Date(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return new Date(b.assignedAt || 0) - new Date(a.assignedAt || 0);
  };

  const inProgress = open.filter((i) => i.status === "in-progress");
  if (inProgress.length) return inProgress.sort(byDueDate)[0];

  const overdue = open.filter(isOverdue);
  if (overdue.length) return overdue.sort(byDueDate)[0];

  return open.sort(byDueDate)[0];
};

/**
 * Roll the plan up into the three states the progress donut shows. Derived
 * entirely from the server-decided per-item `status`, so the donut can never
 * claim more progress than the backend granted.
 */
export const summarisePlan = (items = []) => {
  const counts = { completed: 0, "in-progress": 0, assigned: 0 };
  items.forEach((i) => {
    counts[i.status] = (counts[i.status] || 0) + 1;
  });
  const total = items.length;
  return {
    ...counts,
    total,
    percentComplete: total > 0 ? Math.round((counts.completed / total) * 100) : 0,
  };
};
